import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase';
import {
  collection,
  query,
  where,
  orderBy,
  limit,
  getDocs,
  addDoc,
  updateDoc,
  doc,
  setDoc,
  Timestamp
} from 'firebase/firestore';

// Services pour les fonctions Cloud
export const subscriptionService = {
  // Initialiser les plans d'abonnement (admin seulement)
  initializePlans: () => {
    const initializePlansFunction = httpsCallable(functions, 'initializeSubscriptionPlans');
    return initializePlansFunction({});
  },

  // Créer un abonnement
  createSubscription: (data) => {
    const createSubscriptionFunction = httpsCallable(functions, 'createEmployeeSubscription');
    return createSubscriptionFunction(data);
  },

  // Annuler un abonnement
  cancelSubscription: (data) => {
    const cancelSubscriptionFunction = httpsCallable(functions, 'cancelEmployeeSubscription');
    return cancelSubscriptionFunction(data);
  },

  // Vérifier l'accès de l'utilisateur
  checkAccess: () => {
    const checkAccessFunction = httpsCallable(functions, 'checkSubscriptionAccess');
    return checkAccessFunction({});
  },

  // Marquer une facture comme payée (admin seulement)
  markInvoiceAsPaid: (data) => {
    const markInvoicePaidFunction = httpsCallable(functions, 'markInvoiceAsPaid');
    return markInvoicePaidFunction(data);
  }
};

// Service pour la gestion des données Firestore
export const subscriptionDataService = {
  // Récupérer tous les plans d'abonnement
  getSubscriptionPlans: async (db) => {
    const q = query(
      collection(db, 'subscriptionPlans'),
      where('isActive', '==', true),
      orderBy('price', 'asc')
    );
    
    const snapshot = await getDocs(q);
    return snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data()
    }));
  },

  // Récupérer l'abonnement actuel d'un intervenant
  getCurrentSubscription: async (db, employeeId) => {
    const q = query(
      collection(db, 'employeeSubscriptions'),
      where('employeeId', '==', employeeId),
      where('status', 'in', ['active', 'free', 'offered']),
      orderBy('currentPeriodEnd', 'desc'),
      limit(1)
    );

    const snapshot = await getDocs(q);
    if (snapshot.empty) return null;

    const subscriptionDoc = snapshot.docs[0];
    const data = subscriptionDoc.data();

    // Convertir les Timestamps Firestore en objets Date JavaScript
    return {
      id: subscriptionDoc.id,
      ...data,
      currentPeriodStart: data.currentPeriodStart?.toDate ? data.currentPeriodStart.toDate() : data.currentPeriodStart,
      currentPeriodEnd: data.currentPeriodEnd?.toDate ? data.currentPeriodEnd.toDate() : data.currentPeriodEnd,
      createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : data.createdAt,
      updatedAt: data.updatedAt?.toDate ? data.updatedAt.toDate() : data.updatedAt
    };
  },

  // Récupérer les factures d'un intervenant
  getEmployeeInvoices: async (db, employeeId) => {
    const q = query(
      collection(db, 'invoices'),
      where('employeeId', '==', employeeId),
      orderBy('createdAt', 'desc')
    );
    
    const snapshot = await getDocs(q);
    return snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data()
    }));
  },

  // Récupérer toutes les factures (admin seulement)
  getAllInvoices: async (db) => {
    const q = query(
      collection(db, 'invoices'),
      orderBy('createdAt', 'desc')
    );

    const snapshot = await getDocs(q);
    return snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data()
    }));
  },

  // Vérifier si l'utilisateur a déjà utilisé l'essai gratuit
  hasUsedFreeTrial: async (db, employeeId) => {
    try {
      const q = query(
        collection(db, 'employeeSubscriptions'),
        where('employeeId', '==', employeeId),
        where('status', '==', 'free')
      );

      const snapshot = await getDocs(q);
      return !snapshot.empty;
    } catch (error) {
      console.error('Erreur lors de la vérification de l\'essai gratuit:', error);
      return false;
    }
  },

  // Récupérer l'historique complet des abonnements d'un intervenant
  getSubscriptionHistory: async (db, employeeId) => {
    try {
      const q = query(
        collection(db, 'employeeSubscriptions'),
        where('employeeId', '==', employeeId),
        orderBy('createdAt', 'desc')
      );

      const snapshot = await getDocs(q);
      return snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
        currentPeriodStart: doc.data().currentPeriodStart?.toDate ? doc.data().currentPeriodStart.toDate() : doc.data().currentPeriodStart,
        currentPeriodEnd: doc.data().currentPeriodEnd?.toDate ? doc.data().currentPeriodEnd.toDate() : doc.data().currentPeriodEnd,
        createdAt: doc.data().createdAt?.toDate ? doc.data().createdAt.toDate() : doc.data().createdAt,
        updatedAt: doc.data().updatedAt?.toDate ? doc.data().updatedAt.toDate() : doc.data().updatedAt,
        cancelledAt: doc.data().cancelledAt?.toDate ? doc.data().cancelledAt.toDate() : doc.data().cancelledAt
      }));
    } catch (error) {
      console.error('Erreur lors de la récupération de l\'historique:', error);
      return [];
    }
  },

  // Attribuer un plan gratuit à un intervenant
  assignFreePlan: async (db, employeeId, planId) => {
    console.log('🎁 Attribution du plan gratuit:', { employeeId, planId });

    try {
      // Vérifier si l'utilisateur a déjà un abonnement actif
      const existingSubscription = await subscriptionDataService.getCurrentSubscription(db, employeeId);

      if (existingSubscription) {
        console.log('⚠️ L\'utilisateur a déjà un abonnement actif:', existingSubscription);
        throw new Error('Vous avez déjà un abonnement actif');
      }

      // Vérifier si l'utilisateur a déjà utilisé l'essai gratuit (même expiré)
      const allSubscriptions = await getDocs(
        query(
          collection(db, 'employeeSubscriptions'),
          where('employeeId', '==', employeeId),
          where('status', '==', 'free')
        )
      );

      if (!allSubscriptions.empty) {
        console.log('⚠️ L\'utilisateur a déjà utilisé son essai gratuit');
        throw new Error('Vous avez déjà utilisé votre période d\'essai gratuite. Veuillez souscrire à un abonnement payant pour continuer.');
      }

      // Créer le nouvel abonnement gratuit avec des Timestamps Firestore
      const now = Timestamp.now();
      const endDate = Timestamp.fromMillis(Date.now() + 30 * 24 * 60 * 60 * 1000); // 30 jours

      const subscriptionData = {
        employeeId,
        planId,
        status: 'free',
        currentPeriodStart: now,
        currentPeriodEnd: endDate,
        price: 0,
        createdAt: now,
        updatedAt: now
      };

      console.log('📝 Création de l\'abonnement avec les données:', {
        ...subscriptionData,
        currentPeriodStart: now.toDate(),
        currentPeriodEnd: endDate.toDate()
      });

      const subscriptionRef = await addDoc(collection(db, 'employeeSubscriptions'), subscriptionData);

      console.log('✅ Abonnement gratuit créé avec succès, ID:', subscriptionRef.id);

      return {
        id: subscriptionRef.id,
        employeeId,
        planId,
        status: 'free',
        currentPeriodStart: now.toDate(),
        currentPeriodEnd: endDate.toDate(),
        price: 0,
        createdAt: now.toDate(),
        updatedAt: now.toDate()
      };
    } catch (error) {
      console.error('❌ Erreur lors de l\'attribution du plan gratuit:', error);
      throw error;
    }
  },

  // Mettre à jour le statut d'un abonnement
  updateSubscriptionStatus: async (db, subscriptionId, status) => {
    await updateDoc(doc(db, 'employeeSubscriptions', subscriptionId), {
      status,
      updatedAt: Timestamp.now()
    });
  },

  // Corriger les dates d'un abonnement existant (utilitaire de débogage)
  fixSubscriptionDates: async (db, subscriptionId) => {
    console.log('🔧 Correction des dates de l\'abonnement:', subscriptionId);

    const now = Timestamp.now();
    const endDate = Timestamp.fromMillis(Date.now() + 365 * 24 * 60 * 60 * 1000); // 365 jours

    await updateDoc(doc(db, 'employeeSubscriptions', subscriptionId), {
      currentPeriodStart: now,
      currentPeriodEnd: endDate,
      updatedAt: now
    });

    console.log('✅ Dates corrigées:', {
      currentPeriodStart: now.toDate(),
      currentPeriodEnd: endDate.toDate()
    });
  },

  // Attribuer un plan offert à un intervenant (pour les utilisateurs existants)
  assignOfferedPlan: async (db, employeeId, planId) => {
    console.log('🎁 Attribution du plan offert:', { employeeId, planId });

    try {
      // Vérifier si l'utilisateur a déjà un abonnement actif
      const existingSubscription = await subscriptionDataService.getCurrentSubscription(db, employeeId);

      if (existingSubscription) {
        console.log('⚠️ L\'utilisateur a déjà un abonnement actif:', existingSubscription);
        throw new Error('Vous avez déjà un abonnement actif');
      }

      // Créer le nouvel abonnement offert avec des Timestamps Firestore
      const now = Timestamp.now();
      const endDate = Timestamp.fromMillis(Date.now() + 365 * 24 * 60 * 60 * 1000); // 365 jours

      const subscriptionData = {
        employeeId,
        planId,
        status: 'offered', // Statut spécial pour les utilisateurs existants
        currentPeriodStart: now,
        currentPeriodEnd: endDate,
        price: 0,
        createdAt: now,
        updatedAt: now
      };

      console.log('📝 Création de l\'abonnement offert avec les données:', {
        ...subscriptionData,
        currentPeriodStart: now.toDate(),
        currentPeriodEnd: endDate.toDate()
      });

      const subscriptionRef = await addDoc(collection(db, 'employeeSubscriptions'), subscriptionData);

      console.log('✅ Abonnement offert créé avec succès, ID:', subscriptionRef.id);

      return {
        id: subscriptionRef.id,
        employeeId,
        planId,
        status: 'offered',
        currentPeriodStart: now.toDate(),
        currentPeriodEnd: endDate.toDate(),
        price: 0,
        createdAt: now.toDate(),
        updatedAt: now.toDate()
      };
    } catch (error) {
      console.error('❌ Erreur lors de l\'attribution du plan offert:', error);
      throw error;
    }
  },

  // Créer un paiement d'abonnement
  createSubscriptionPayment: async (db, employeeId, planId, paymentMethodId) => {
    console.log('💳 Création du paiement d\'abonnement:', { employeeId, planId, paymentMethodId });

    try {
      // Utiliser la Cloud Function pour créer l'abonnement Stripe
      const createSubscriptionFunction = httpsCallable(functions, 'createEmployeeSubscription');
      const result = await createSubscriptionFunction({
        planId,
        paymentMethodId
      });

      console.log('✅ Réponse complète de la Cloud Function:', result);
      console.log('📊 Données de la réponse:', result.data);

      if (!result.data.success) {
        console.error('❌ Erreur dans la réponse de la Cloud Function:', result.data);
        throw new Error(result.data.error || 'Erreur lors de la création de l\'abonnement');
      }

      // Log détaillé du client_secret
      console.log('🔑 Client secret reçu:', result.data.clientSecret ? 'PRÉSENT' : 'ABSENT');
      console.log('📋 Statut de l\'abonnement:', result.data.status);
      console.log('🆔 ID de l\'abonnement:', result.data.subscriptionId);

      return {
        success: result.data.success,
        clientSecret: result.data.clientSecret,
        subscription: result.data.subscription,
        subscriptionId: result.data.subscriptionId,
        stripeSubscriptionId: result.data.stripeSubscriptionId,
        status: result.data.status,
        message: result.data.message
      };
    } catch (error) {
      console.error('❌ Erreur lors de la création du paiement:', error);

      // Si les Cloud Functions ne sont pas déployées, donner un message explicite
      if (error.code === 'functions/not-found') {
        throw new Error('Les fonctions de paiement ne sont pas déployées. Veuillez contacter l\'administrateur.');
      }

      throw error;
    }
  },

  // Désactiver un abonnement (pour les tests)
  deactivateSubscription: async (db, subscriptionId) => {
    console.log('🚫 Désactivation de l\'abonnement:', subscriptionId);

    try {
      await updateDoc(doc(db, 'employeeSubscriptions', subscriptionId), {
        status: 'cancelled',
        updatedAt: Timestamp.now(),
        cancelledAt: Timestamp.now()
      });

      console.log('✅ Abonnement désactivé avec succès');

      return {
        success: true,
        message: 'Abonnement désactivé avec succès'
      };
    } catch (error) {
      console.error('❌ Erreur lors de la désactivation de l\'abonnement:', error);
      throw error;
    }
  },

  // Initialiser les plans d'abonnement manuellement (sans Stripe, pour démarrer)
  initializePlansManually: async (db) => {
    console.log('🚀 Initialisation manuelle des plans d\'abonnement...');

    try {
      const now = Timestamp.now();

      // Plans à créer avec IDs fixes
      const plans = [
        {
          id: 'free',
          name: 'Découverte',
          description: 'Testez toutes les fonctionnalités gratuitement pendant 30 jours',
          price: 0,
          billingInterval: 'month',
          features: [
            'Accès complet à toutes les fonctionnalités',
            'Planning personnel (4 vues)',
            'Gestion de vos rendez-vous',
            'Pointage GPS et NFC',
            'Messagerie avec bénéficiaires et intervenants',
            'Conversations 1-to-1 et groupes',
            'Application mobile PWA',
            'Mode hors ligne',
            'Support par email (48h)',
            'Aucune carte bancaire requise'
          ],
          isActive: true,
          isFree: true,
          createdAt: now,
          updatedAt: now
        },
        {
          id: 'monthly',
          name: 'Essentiel',
          description: 'Accès complet avec facturation mensuelle',
          price: 1500,
          billingInterval: 'month',
          stripePriceId: null, // Sera ajouté plus tard lors de la config Stripe
          features: [
            'Accès complet à toutes les fonctionnalités',
            'Planning personnel (4 vues)',
            'Gestion de vos rendez-vous',
            'Pointage GPS et NFC',
            'Messagerie avec bénéficiaires et intervenants',
            'Conversations 1-to-1 et groupes',
            'Application mobile PWA',
            'Mode hors ligne',
            'Support prioritaire (24h)',
            'Facturation mensuelle'
          ],
          isActive: true,
          isFree: false,
          createdAt: now,
          updatedAt: now
        },
        {
          id: 'yearly',
          name: 'Essentiel Annuel',
          description: 'Accès complet avec facturation annuelle - Économisez 84€',
          price: 9600,
          billingInterval: 'year',
          stripePriceId: null, // Sera ajouté plus tard lors de la config Stripe
          features: [
            'Accès complet à toutes les fonctionnalités',
            'Planning personnel (4 vues)',
            'Gestion de vos rendez-vous',
            'Pointage GPS et NFC',
            'Messagerie avec bénéficiaires et intervenants',
            'Conversations 1-to-1 et groupes',
            'Application mobile PWA',
            'Mode hors ligne',
            'Support prioritaire (24h)',
            'Facturation annuelle',
            'Économie de 84€ par an'
          ],
          isActive: true,
          isFree: false,
          createdAt: now,
          updatedAt: now
        },
        {
          id: 'offered',
          name: 'Premium Offert',
          description: 'Accès premium offert par l\'entreprise',
          price: 0,
          billingInterval: 'month',
          features: [
            'Accès complet à toutes les fonctionnalités',
            'Planning personnel (4 vues)',
            'Gestion de vos rendez-vous',
            'Pointage GPS et NFC',
            'Messagerie avec bénéficiaires et intervenants',
            'Conversations 1-to-1 et groupes',
            'Application mobile PWA',
            'Mode hors ligne',
            'Support prioritaire (24h)',
            'Offert par votre entreprise'
          ],
          isActive: true,
          isFree: false,
          isOffered: true,
          createdAt: now,
          updatedAt: now
        }
      ];

      // Créer chaque plan dans Firestore
      for (const plan of plans) {
        const planId = plan.id;
        const planData = { ...plan };
        delete planData.id; // Retirer l'ID des données

        await setDoc(doc(db, 'subscriptionPlans', planId), planData);
        console.log(`✅ Plan créé: ${planId} - ${plan.name}`);
      }

      console.log('✅ Tous les plans ont été initialisés avec succès !');

      return {
        success: true,
        message: 'Plans d\'abonnement initialisés avec succès',
        plansCreated: plans.length
      };
    } catch (error) {
      console.error('❌ Erreur lors de l\'initialisation des plans:', error);
      throw error;
    }
  }
};

// Utilitaires pour les abonnements
export const subscriptionUtils = {
  // Formater le prix en euros
  formatPrice: (priceInCents) => {
    return (priceInCents / 100).toFixed(2) + ' €';
  },

  // Calculer l'économie annuelle
  calculateYearlySavings: (monthlyPrice, yearlyPrice) => {
    const monthlyTotal = monthlyPrice * 12;
    return monthlyTotal - yearlyPrice;
  },

  // Vérifier si un abonnement est actif
  isSubscriptionActive: (subscription) => {
    if (!subscription) return false;

    const now = new Date();

    // Convertir currentPeriodEnd en Date si c'est un Timestamp Firestore
    let endDate = subscription.currentPeriodEnd;
    if (endDate?.toDate) {
      endDate = endDate.toDate();
    } else if (!(endDate instanceof Date)) {
      endDate = new Date(endDate);
    }

    console.log('🔍 Vérification abonnement actif:', {
      status: subscription.status,
      currentPeriodEnd: endDate,
      now: now,
      isActive: (['active', 'free', 'offered'].includes(subscription.status)) && endDate >= now
    });

    return (
      ['active', 'free', 'offered'].includes(subscription.status) &&
      endDate >= now
    );
  },

  // Vérifier si un abonnement est gratuit
  isFreeSubscription: (subscription) => {
    return subscription?.status === 'free' || false;
  },

  // Vérifier si un abonnement est offert
  isOfferedSubscription: (subscription) => {
    return subscription?.status === 'offered' || false;
  },

  // Obtenir le statut d'accès
  getAccessStatus: (subscription) => {
    const isActive = subscriptionUtils.isSubscriptionActive(subscription);
    const isFree = subscriptionUtils.isFreeSubscription(subscription);
    const isOffered = subscriptionUtils.isOfferedSubscription(subscription);

    return {
      hasAccess: isActive,
      isFree,
      isOffered,
      isPremium: isActive && !isFree && !isOffered,
      needsSubscription: !isActive
    };
  },

  // Générer un message d'upsell
  getUpsellMessage: (plan) => {
    if (plan.billingInterval === 'year') {
      const monthlyEquivalent = plan.price / 12 / 100;
      const savings = 15 - monthlyEquivalent; // 15€ - prix mensuel équivalent
      return `Économisez ${savings.toFixed(2)}€ par mois avec l'abonnement annuel !`;
    }
    
    return 'Accédez à toutes les fonctionnalités premium';
  }
};

// Hook personnalisé pour la gestion des abonnements
export const useSubscription = (db, employeeId) => {
  const checkAccess = async () => {
    try {
      const result = await subscriptionService.checkAccess();
      return result.data;
    } catch (error) {
      console.error('Erreur lors de la vérification de l\'accès:', error);
      return { hasAccess: false, subscription: null, plan: null, isFree: false };
    }
  };

  const getCurrentSubscription = async () => {
    return await subscriptionDataService.getCurrentSubscription(db, employeeId);
  };

  const getSubscriptionPlans = async () => {
    return await subscriptionDataService.getSubscriptionPlans(db);
  };

  const createSubscription = async (planId, paymentMethodId) => {
    return await subscriptionService.createSubscription({ planId, paymentMethodId });
  };

  const cancelSubscription = async (subscriptionId) => {
    return await subscriptionService.cancelSubscription({ subscriptionId });
  };

  return {
    checkAccess,
    getCurrentSubscription,
    getSubscriptionPlans,
    createSubscription,
    cancelSubscription
  };
};
